import React, { useCallback, type Dispatch, type RefObject } from 'react'
import { Button } from 'devextreme-react'
import { SchedulerRef } from 'devextreme-react/scheduler'
import { ClickEvent } from 'devextreme/ui/button'
import { formatDate } from 'devextreme/localization'
import type { AzureUserInfo } from '@/auth/azure/azureManager'
import { htmlToPlainText } from '@/routes/utils'
import {
  buildEditData,
  canManageAppointment,
  colorWithAlpha,
  getRecurrenceLabel,
} from '@/routes/calendar/CalendarPage.utils'
import {
  type AppointmentProps,
  type CalendarRoom,
  type ExtendedAppointment,
  type popupStateType,
  type SchedulerViewType,
} from '@/routes/calendar/CalendarPage.types'
import type { user } from '@/model/qsadminapi/QsAdminApiModuleModel'

export type AppointmentComponentsParams = {
  usersWithColors: ((user | CalendarRoom) & { color?: string })[]
  userInfo: AzureUserInfo | undefined
  schedulerRef: RefObject<SchedulerRef | null>
  dispatch: Dispatch<popupStateType>
  pendingShowRef: React.MutableRefObject<boolean>
  loadWithPending: (pendingItems: ExtendedAppointment[]) => void
  requestDeleteAppointment: (appointment: ExtendedAppointment) => void
  requestEditAppointment: (appointment: ExtendedAppointment) => void
  currentSchedulerView: SchedulerViewType
  showAppointmentLabels: boolean
}

export const useAppointmentComponents = ({
  usersWithColors,
  userInfo,
  schedulerRef,
  dispatch,
  pendingShowRef,
  loadWithPending,
  requestDeleteAppointment,
  requestEditAppointment,
  currentSchedulerView,
  showAppointmentLabels,
}: AppointmentComponentsParams) => {
  const Appointment = useCallback(
    (props: AppointmentProps) => {
      const { targetedAppointmentData } = props.data
      const foundUser = usersWithColors.find((item) => item.email === targetedAppointmentData.email)
      const status = targetedAppointmentData.status
      const recurrenceLabel = getRecurrenceLabel(targetedAppointmentData.recurrenceType)
      const isMonthView = currentSchedulerView === 'month'
      const startTime = formatDate(new Date(targetedAppointmentData.startDate ?? ''), 'shortTime')
      const endTime = formatDate(new Date(targetedAppointmentData.endDate ?? ''), 'shortTime')
      let mainDivStyle: React.CSSProperties = { backgroundColor: colorWithAlpha(foundUser?.color, 0.5) }
      switch (status) {
        case 'Tentative':
          mainDivStyle = {
            borderLeft: `6px solid`,
            borderImage: 'repeating-linear-gradient(45deg, white, white 5px, transparent 5px, transparent 10px) 8',
            paddingLeft: '4px',
            backgroundColor: colorWithAlpha(foundUser?.color, 0.5),
          }
          break
        case 'Free': {
          const uc = foundUser?.color ?? 'transparent'
          mainDivStyle = {
            // Layer 1 (top): striscia 6×1px user color — bordo top della cornice
            // Layer 2 (bottom): striscia 6×1px user color — bordo bottom della cornice
            // Layer 3: profilo orizzontale — left 1px | theme fill 4px | right 1px | sfondo app
            background: [
              `linear-gradient(${uc},${uc}) 0 0/6px 1px no-repeat`,
              `linear-gradient(${uc},${uc}) 0 100%/6px 1px no-repeat`,
              `linear-gradient(to right,${uc} 1px,var(--dx-color-main-bg,#fff) 1px,var(--dx-color-main-bg,#fff) 5px,${uc} 5px,${uc} 6px,${colorWithAlpha(uc, 0.5)} 6px)`,
            ].join(','),
            paddingLeft: '10px',
          }
          break
        }
        case 'Busy':
          mainDivStyle = {
            borderLeft: `6px solid ${foundUser?.color}`,
            paddingLeft: '4px',
            backgroundColor: colorWithAlpha(foundUser?.color, 0.5),
          }
          break
        default:
          break
      }
      return (
        <div style={mainDivStyle} className="showtime-preview">
          {showAppointmentLabels && foundUser?.fullname !== targetedAppointmentData.text && (
            <div className="appointment-user">
              <strong>{foundUser?.fullname}</strong>
            </div>
          )}
          <div className="appointment-subject">
            <strong>
              {isMonthView && <span className="appointment-start-time">{startTime}</span>}
              {targetedAppointmentData.text}
              {recurrenceLabel && (
                <span
                  className={`appointment-recurrence-icon${targetedAppointmentData.recurrenceType === 'Exception' ? ' appointment-recurrence-icon--exception' : ''}`}
                  title={recurrenceLabel}
                >
                  <i className="dx-icon-repeat" />
                </span>
              )}
              {targetedAppointmentData.hasAttachments && (
                <span className="appointment-attachment-icon" title="Contiene allegati">
                  <i className="dx-icon-attach" />
                </span>
              )}{' '}
              {status === 'Tentative' && <b>(Da confermare)</b>} {status === 'Available' && <b>(Disponibile)</b>}
            </strong>
          </div>
          <div className="appointment-location">{targetedAppointmentData.location?.displayName}</div>
          {!isMonthView && (
            <div className="appointment-duration">
              {startTime}
              {' - '}
              {endTime}
            </div>
          )}
          {(() => {
            const plainText = targetedAppointmentData.description
              ? htmlToPlainText(targetedAppointmentData.description).trim()
              : ''
            return plainText ? <div className="appointment-description">{plainText}</div> : null
          })()}
        </div>
      )
    },
    [currentSchedulerView, showAppointmentLabels, usersWithColors],
  )

  const AppointmentTooltip = useCallback(
    (props: AppointmentProps) => {
      const { targetedAppointmentData } = props.data
      const status = targetedAppointmentData.status
      const foundUser = usersWithColors.find((item) => item.email === targetedAppointmentData.email)
      const startDate = new Date(targetedAppointmentData.startDate ?? '')
      const endDate = new Date(targetedAppointmentData.endDate ?? '')
      const buttonEnabled = canManageAppointment(
        userInfo?.roles,
        userInfo?.email,
        foundUser?.email,
        targetedAppointmentData.isOrganizer,
      )
      const recurrenceLabel = getRecurrenceLabel(targetedAppointmentData.recurrenceType)
      const formatter = new Intl.DateTimeFormat('it-IT', {
        timeZone: 'Europe/Rome',
        year: startDate.getDate() === endDate.getDate() ? undefined : 'numeric',
        month: startDate.getDate() === endDate.getDate() ? undefined : '2-digit',
        day: startDate.getDate() === endDate.getDate() ? undefined : '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })

      let markerStyle: React.CSSProperties = { backgroundColor: foundUser?.color }
      switch (status) {
        case 'Tentative':
          markerStyle = {
            backgroundImage: `repeating-linear-gradient(
              90deg,
              ${foundUser?.color} 0,
              ${foundUser?.color} 2px,
              white 2px,
              white 4px
            )`,
            backgroundSize: '8px 8px',
          }
          break
        case 'Free':
          markerStyle = {
            width: '18px', // Set the size of the circle
            height: '18px',
            borderRadius: '50%', // Make it round
            border: `4px solid ${foundUser?.color}`, // Set border color
            backgroundColor: 'var(--dx-color-main-bg, #fff)', // Inner white background
          }
          break
        default:
          break
      }

      return (
        <>
          <div className={'dx-tooltip-appointment-item'}>
            <div className={'dx-tooltip-appointment-item-marker'}>
              <div className={'dx-tooltip-appointment-item-marker-body'} style={markerStyle}></div>
            </div>
            <div className={'dx-tooltip-appointment-item-content'}>
              {foundUser?.fullname !== targetedAppointmentData.text && (
                <div className={'dx-tooltip-appointment-item-content-email'}>
                  <strong>{foundUser?.fullname}</strong>
                </div>
              )}
              <div className={'dx-tooltip-appointment-item-content-subject'}>
                <strong>{targetedAppointmentData.text}</strong>
              </div>
              <div className={'dx-tooltip-appointment-item-content-date'}>
                {status === 'Tentative' && <b>Da confermare</b>} {status === 'Free' && <b>Disponibile</b>}
                {recurrenceLabel && (
                  <span
                    className={`appointment-recurrence-badge${targetedAppointmentData.recurrenceType === 'Exception' ? ' appointment-recurrence-badge--exception' : ''}`}
                  >
                    <i className="dx-icon-repeat" /> {recurrenceLabel}
                  </span>
                )}
                <div>{targetedAppointmentData.location?.displayName}</div>
                {!targetedAppointmentData.allDay && (
                  <div>
                    {formatter.format(startDate)}
                    {' - '}
                    {formatter.format(endDate)}
                  </div>
                )}
                {(() => {
                  const plainText = targetedAppointmentData.description
                    ? htmlToPlainText(targetedAppointmentData.description).trim()
                    : ''
                  return plainText ? (
                    <div className={'dx-tooltip-appointment-item-content-description'}>{plainText}</div>
                  ) : null
                })()}
              </div>
            </div>

            <div className={'dx-tooltip-appointment-item-delete-button-container'}>
              <Button
                className={'dx-tooltip-appointment-item-delete-button'}
                icon="video"
                hint="Partecipa alla riunione Teams"
                stylingMode="text"
                visible={
                  foundUser?.email === userInfo?.email &&
                  !!(targetedAppointmentData.isOnlineMeeting && targetedAppointmentData.teamsJoinUrl)
                }
                onClick={(e: ClickEvent) => {
                  e.event?.stopPropagation()
                  window.open(targetedAppointmentData.teamsJoinUrl, '_blank', 'noopener,noreferrer')
                }}
              />
            </div>

            <div className={'dx-tooltip-appointment-item-delete-button-container'}>
              <Button
                className={'dx-tooltip-appointment-item-delete-button'}
                icon={buttonEnabled ? 'edit' : 'eyeopen'}
                hint={buttonEnabled ? 'Modifica' : 'Visualizza'}
                stylingMode="text"
                onClick={(e: ClickEvent) => {
                  e.event?.stopPropagation()
                  schedulerRef.current?.instance().hideAppointmentTooltip()
                  requestEditAppointment(targetedAppointmentData)
                }}
              ></Button>
            </div>

            <div className={'dx-tooltip-appointment-item-delete-button-container'}>
              <Button
                className={'dx-tooltip-appointment-item-delete-button'}
                icon="copy"
                hint="Duplica"
                stylingMode="text"
                visible={buttonEnabled}
                onClick={(e: ClickEvent) => {
                  e.event?.stopPropagation()
                  schedulerRef.current?.instance().hideAppointmentTooltip()
                  dispatch({
                    // La copia è sempre un evento proprio: l'eventuale flag di invito non va ereditato
                    editData: buildEditData(targetedAppointmentData, {
                      appointmentId: undefined,
                      isOrganizer: undefined,
                      organizer: undefined,
                    }),
                    creating: true,
                  })
                  pendingShowRef.current = true
                }}
              />
            </div>

            <div className={'dx-tooltip-appointment-item-delete-button-container'}>
              <Button
                className={'dx-tooltip-appointment-item-delete-button'}
                visible={buttonEnabled}
                icon="trash"
                hint="Elimina"
                stylingMode="text"
                onClick={(e: ClickEvent) => {
                  e.event?.stopPropagation()
                  schedulerRef.current?.instance().hideAppointmentTooltip()
                  requestDeleteAppointment(targetedAppointmentData)
                }}
              ></Button>
            </div>
          </div>
        </>
      )
    },
    [usersWithColors, userInfo?.email, userInfo?.roles],
  )

  return { Appointment, AppointmentTooltip }
}
